/**
 * The editor class name
 */
var EDITOR_CLASS_NAME = 'remirror-editor';
/**
 * The editor class selector
 */

var EDITOR_CLASS_SELECTOR = /*#__PURE__*/".".concat(EDITOR_CLASS_NAME);
/**
 * The css class added to a node that is selected.
 */

var SELECTED_NODE_CLASS_NAME = 'ProseMirror-selectednode';
/**
 * The css selector for a selected node.
 */

var SELECTED_NODE_CLASS_SELECTOR = /*#__PURE__*/".".concat(SELECTED_NODE_CLASS_NAME);
/**
 * ProseMirror uses the Unicode Character 'OBJECT REPLACEMENT CHARACTER'
 * (U+FFFC) as text representation for leaf nodes, i.e. nodes that don't have
 * any content or text property (e.g. hardBreak, emoji, mention, rule) It was
 * introduced because of https://github.com/ProseMirror/prosemirror/issues/262
 * This can be used in an input rule regex to be able to include or exclude such
 * nodes.
 */

var LEAF_NODE_REPLACING_CHARACTER = "\uFFFC";
/**
 * The null character.
 *
 * See {@link https://stackoverflow.com/a/6380172}
 */

var NULL_CHARACTER = '\0';
/**
 * A character useful for separating inline nodes.
 *
 * @remarks
 * Typically used in decorations as follows.
 *
 * ```ts
 * document.createTextNode(ZERO_WIDTH_SPACE_CHAR);
 * ```
 *
 * This produces the html entity '8203'
 */

var ZERO_WIDTH_SPACE_CHAR = "\u200B";
/**
 * The non breaking space character.
 */

var NON_BREAKING_SPACE_CHAR = "\xA0";
/**
 * A default empty object node. Useful for resetting the content of a
 * prosemirror document.
 */

var EMPTY_PARAGRAPH_NODE = {
  type: 'doc',
  content: [{
    type: 'paragraph'
  }]
};
var EMPTY_NODE = {
  type: 'doc',
  content: []
};
/**
 * Marks are categorized into different groups. One motivation for this was to
 * allow the `code` mark to exclude other marks, without needing to explicitly
 * name them. Explicit naming requires the named mark to exist in the schema.
 * This is undesirable because we want to construct different schemas that have
 * different sets of nodes/marks.
 */

var MarkGroup;

(function (MarkGroup) {
  MarkGroup["FontStyle"] = "fontStyle";
  MarkGroup["Link"] = "link";
  MarkGroup["Color"] = "color";
  MarkGroup["Alignment"] = "alignment";
  MarkGroup["Indentation"] = "indentation";
  MarkGroup["Behavior"] = "behavior";
  MarkGroup["Code"] = "code";
})(MarkGroup || (MarkGroup = {}));

var NodeGroup;
/**
 * Defines the type of the extension.
 */

(function (NodeGroup) {
  NodeGroup["Inline"] = "inline";
  NodeGroup["Block"] = "block";
})(NodeGroup || (NodeGroup = {}));

var ExtensionType;
/**
 * These are the default supported tag strings which help categorize different
 * behaviors that extensions can exhibit.
 *
 * @remarks
 *
 * Any extension can register itself with multiple such behaviors and these
 * categorizations can be used by other extensions when running commands and
 * updating the document.
 */

(function (ExtensionType) {
  ExtensionType["Node"] = "node";
  ExtensionType["Mark"] = "mark";
  ExtensionType["Plain"] = "plain";
})(ExtensionType || (ExtensionType = {}));

var ExtensionTag;
/**
 * The identifier key which is used to check objects for whether they are a
 * certain type.
 *
 * @remarks
 *
 * Just pretend you don't know this exists.
 *
 * @internal
 */

(function (ExtensionTag) {
  ExtensionTag["LastNodeCompatible"] = "lastNodeCompatible";
  ExtensionTag["FormattingMark"] = "formattingMark";
  ExtensionTag["FormattingNode"] = "formattingNode";
  ExtensionTag["NodeCursor"] = "nodeCursor";
})(ExtensionTag || (ExtensionTag = {}));

var __INTERNAL_REMIRROR_IDENTIFIER_KEY__ = /*#__PURE__*/Symbol.for('__remirror__');
/**
 * These constants are stored on the `REMIRROR_IDENTIFIER_KEY` property of
 * `remirror` related constructors and instances in order to identify them as
 * being internal to Remirror.
 *
 * @remarks
 *
 * This helps to prevent issues around check types via `instanceof` which can
 * lead to false negatives.
 *
 * @internal
 */

var RemirrorIdentifier;
/**
 * The priority of extension which determines what order it is loaded into the
 * editor.
 *
 * @remarks
 *
 * Higher priority extension (higher numberic value) will ensure the extension
 * has a higher preference in your editor. In the case where you load two
 * identical extensions into your editor (same name, or same constructor), the
 * extension with the  higher priority is the one that will be loaded.
 *
 * The higher the numeric value the higher the priority. The priority can also
 * be passed a number but naming things in this `enum` should help provide some
 * context to the numbers.
 *
 * By default all extensions are created with a `ExtensionPriority.Default`.
 */

(function (RemirrorIdentifier) {
  RemirrorIdentifier["PlainExtension"] = "RemirrorPlainExtension";
  RemirrorIdentifier["NodeExtension"] = "RemirrorNodeExtension";
  RemirrorIdentifier["MarkExtension"] = "RemirrorMarkExtension";
  RemirrorIdentifier["PlainExtensionConstructor"] = "RemirrorPlainExtensionConstructor";
  RemirrorIdentifier["NodeExtensionConstructor"] = "RemirrorNodeExtensionConstructor";
  RemirrorIdentifier["MarkExtensionConstructor"] = "RemirrorMarkExtensionConstructor";
  RemirrorIdentifier["Manager"] = "RemirrorManager";
  RemirrorIdentifier["Preset"] = "RemirrorPreset";
  RemirrorIdentifier["PresetConstructor"] = "RemirrorPresetConstructor";
})(RemirrorIdentifier || (RemirrorIdentifier = {}));

var ExtensionPriority;
/**
 * Identifies the stage the extension manager is at.
 */

(function (ExtensionPriority) {
  ExtensionPriority[ExtensionPriority["Critical"] = 1000000] = "Critical";
  ExtensionPriority[ExtensionPriority["Highest"] = 100000] = "Highest";
  ExtensionPriority[ExtensionPriority["High"] = 10000] = "High";
  ExtensionPriority[ExtensionPriority["Medium"] = 1000] = "Medium";
  ExtensionPriority[ExtensionPriority["Default"] = 100] = "Default";
  ExtensionPriority[ExtensionPriority["Low"] = 10] = "Low";
  ExtensionPriority[ExtensionPriority["Lowest"] = 0] = "Lowest";
})(ExtensionPriority || (ExtensionPriority = {}));

var ManagerPhase;

(function (ManagerPhase) {
  ManagerPhase[ManagerPhase["None"] = 0] = "None";
  ManagerPhase[ManagerPhase["Create"] = 1] = "Create";
  ManagerPhase[ManagerPhase["EditorView"] = 2] = "EditorView";
  ManagerPhase[ManagerPhase["Runtime"] = 3] = "Runtime";
  ManagerPhase[ManagerPhase["Destroy"] = 4] = "Destroy";
})(ManagerPhase || (ManagerPhase = {}));

/**
 * The error codes for errors used throughout the codebase.
 *
 * @remarks
 *
 * They can be removed but should never be changed since they are also used to
 * reference the errors within search engines.
 */
var ErrorConstant;

(function (ErrorConstant) {
  ErrorConstant["PROD"] = "RMR0000";
  ErrorConstant["UNKNOWN"] = "RMR0001";
  ErrorConstant["INVALID_COMMAND_ARGUMENTS"] = "RMR0002";
  ErrorConstant["CUSTOM"] = "RMR0003";
  ErrorConstant["CORE_HELPERS"] = "RMR0004";
  ErrorConstant["MUTATION"] = "RMR0005";
  ErrorConstant["INTERNAL"] = "RMR0006";
  ErrorConstant["MISSING_REQUIRED_EXTENSION"] = "RMR0007";
  ErrorConstant["MANAGER_PHASE_ERROR"] = "RMR0008";
  ErrorConstant["NEW_EDITOR_MANAGER"] = "RMR0009";
  ErrorConstant["INVALID_PRESET_EXTENSION"] = "RMR0010";
  ErrorConstant["INVALID_MANAGER_ARGUMENTS"] = "RMR0011";
  ErrorConstant["COMMANDS_CALLED_IN_OUTER_SCOPE"] = "RMR0012";
  ErrorConstant["HELPERS_CALLED_IN_OUTER_SCOPE"] = "RMR0013";
  ErrorConstant["INVALID_MANAGER_EXTENSION"] = "RMR0014";
  ErrorConstant["INVALID_MANAGER_PRESET"] = "RMR0015";
  ErrorConstant["DUPLICATE_COMMAND_NAMES"] = "RMR0016";
  ErrorConstant["DUPLICATE_HELPER_NAMES"] = "RMR0017";
  ErrorConstant["NON_CHAINABLE_COMMAND"] = "RMR0018";
  ErrorConstant["INVALID_EXTENSION"] = "RMR0019";
  ErrorConstant["INVALID_PRESET"] = "RMR0020";
  ErrorConstant["INVALID_NAME"] = "RMR0050";
  ErrorConstant["EXTENSION"] = "RMR0100";
  ErrorConstant["EXTENSION_SPEC"] = "RMR0101";
  ErrorConstant["EXTENSION_EXTRA_ATTRIBUTES"] = "RMR0102";
  ErrorConstant["INVALID_SET_EXTENSION_OPTIONS"] = "RMR0103";
  ErrorConstant["REACT_PROVIDER_CONTEXT"] = "RMR0200";
  ErrorConstant["REACT_GET_ROOT_PROPS"] = "RMR0201";
  ErrorConstant["REACT_EDITOR_VIEW"] = "RMR0202";
  ErrorConstant["REACT_CONTROLLED"] = "RMR0203";
  ErrorConstant["REACT_NODE_VIEW"] = "RMR0204";
  ErrorConstant["I18N_CONTEXT"] = "RMR0300";
})(ErrorConstant || (ErrorConstant = {}));

export { EDITOR_CLASS_NAME, EDITOR_CLASS_SELECTOR, EMPTY_NODE, EMPTY_PARAGRAPH_NODE, ErrorConstant, ExtensionPriority, ExtensionTag, ExtensionType, LEAF_NODE_REPLACING_CHARACTER, ManagerPhase, MarkGroup, NON_BREAKING_SPACE_CHAR, NULL_CHARACTER, NodeGroup, RemirrorIdentifier, SELECTED_NODE_CLASS_NAME, SELECTED_NODE_CLASS_SELECTOR, ZERO_WIDTH_SPACE_CHAR, __INTERNAL_REMIRROR_IDENTIFIER_KEY__ };
